import { Injectable, signal } from '@angular/core';
import { getDocument, PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import * as pdfjsLib from 'pdfjs-dist';
import { Feature, Map, View } from 'ol';
import { Extent, getCenter } from 'ol/extent';
import { Projection } from 'ol/proj';
import ImageLayer from 'ol/layer/Image';
import Static from 'ol/source/ImageStatic';
import UTIF from 'utif';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import OLMap from 'ol/Map';
import { Polygon } from 'ol/geom';
import { Course } from '../../models/transcriptExtractionData.model';
import { inject } from '@angular/core';
import { isSupportedDocumentContent } from '../../utils/documentUtils';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.mjs';

@Injectable()
export class FileLoader {
  extent = [0, 0, 5, 5];
  minimumZoom = 1;
  numPages = signal(1);
  currentPage = signal(1);
  hasAnnotations = signal(false);
  annotationsVisible = signal(true);
  rotation = 0;
  rotationValues = [0, Math.PI / 2, Math.PI, -Math.PI / 2];
  rotationIndex = 0;
  unsupportedFileType = false;
  canvas: HTMLCanvasElement | null = null;
  map: OLMap = new Map();

  public highlightLayer!: VectorLayer<VectorSource>;
  public highlightSource!: VectorSource;
  private lastLoadedBase64?: string;
  private currentLoadId = 0;

  /**
   * Refreshes the current view by reloading the last loaded document
   */
  public refreshView(): void {
    if (!this.lastLoadedBase64) return;

    const currentPageBackup = this.currentPage();

    switch (this.lastLoadedBase64.charAt(0)) {
      case 'J':
        this.loadPdf(this.lastLoadedBase64);
        break;
      case 'S':
        this.loadTiff(this.lastLoadedBase64);
        break;
      case 'i':
      case '/':
        this.loadImage(this.lastLoadedBase64.replace(/^data:image\/(png|jpeg);base64,/, ''));
        break;
    }

    this.currentPage.set(currentPageBackup);
  }

  /**
   * Gets the total number of pages in the document
   * @returns The total number of pages
   */
  get getNumPages() {
    return this.numPages();
  }

  /**
   * Gets the current page number
   * @returns The current page number
   */
  get getCurrentPage() {
    return this.currentPage();
  }

  /**
   * Loads a file from base64 content and determines the file type
   * @param base64Content The base64 encoded content of the file
   * @returns True if the file was loaded successfully, false otherwise
   */
  public loadFile(base64Content: string): boolean {
    this.unsupportedFileType = false;
    this.currentPage.set(1);
    this.lastLoadedBase64 = base64Content;
    switch (base64Content.charAt(0)) {
      case 'J': //PDF
        this.loadPdf(base64Content);
        return true;
      case 'i': //PNG
      case '/': //JPEG
        this.numPages.set(1);
        this.loadImage(base64Content);
        return true;
      case 'S': //TIFF
        this.loadTiff(base64Content);
        return true;
      default:
        this.unsupportedFileType = true;
        this.clearMap();
        console.error('Unsupported file type');
        return false;
    }
  }

  /**
   * Loads a PDF document from base64 content
   * @param base64Pdf The base64 encoded PDF content
   */
  public async loadPdf(base64Pdf: string) {
    // Increment load ID to track this specific request
    const loadId = ++this.currentLoadId;

    // Use createMap if map target is not set, otherwise use updateMap
    const needsCreate = !this.map.getTarget() && !this.unsupportedFileType;

    const url = URL.createObjectURL(this.base64ToBlob(base64Pdf));
    const imageUrl = await this.loadPdfAsImage(url);

    // Check if this request is still the latest one
    if (loadId !== this.currentLoadId) {
      return;
    }

    if (needsCreate) {
      this.createMap(imageUrl);
    } else {
      this.updateMap(imageUrl);
    }
  }

  /**
   * Loads an image from base64 content
   * @param base64Image The base64 encoded image content
   */
  private loadImage(base64Image: string) {
    if (this.map.getLayers().getLength() > 0) {
      this.updateMap('data:image/png;base64,' + base64Image);
    } else {
      this.createMap('data:image/png;base64,' + base64Image);
    }
  }

  /**
   * Loads a TIFF document from base64 content
   * @param base64Tiff The base64 encoded TIFF content
   */
  public async loadTiff(base64Tiff: string) {
    const url = URL.createObjectURL(this.base64ToBlob(base64Tiff));
    const imageUrl = await this.loadTiffAsImage(url);
    if (this.map.getLayers().getLength() > 0) {
      this.updateMap(imageUrl);
    } else {
      this.createMap(imageUrl);
    }
  }

  /**
   * Converts a PDF page to an image and returns the data URL
   * @param pdfUrl The URL of the PDF document
   * @returns The data URL of the rendered PDF page
   */
  private async loadPdfAsImage(pdfUrl: string) {
    const loadingTask = getDocument(pdfUrl);
    const pdf = await loadingTask.promise;
    this.numPages.set(pdf.numPages);
    const page = await pdf.getPage(this.currentPage());

    const annotations = await page.getAnnotations();
    this.hasAnnotations.set(annotations.length > 0);

    const viewport = page.getViewport({ scale: 3 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;

    canvas.height = viewport.height;
    canvas.width = viewport.width;
    this.canvas = canvas;

    const annotationMode = this.annotationsVisible()
      ? pdfjsLib.AnnotationMode.ENABLE
      : pdfjsLib.AnnotationMode.DISABLE;

    await page.render({ canvasContext: context, viewport, annotationMode }).promise;
    return canvas.toDataURL();
  }

  /**
   * Converts a TIFF page to an image and returns the data URL
   * @param tifUrl The URL of the TIFF document
   * @returns The data URL of the rendered TIFF page
   */
  private async loadTiffAsImage(tifUrl: string) {
    const response = await fetch(tifUrl);
    const tiffBlob = await response.blob();
    const arrayBuffer = await tiffBlob.arrayBuffer();
    const ifds = UTIF.decode(arrayBuffer);
    this.numPages.set(ifds.length);
    for (const tiffImage of ifds) {
      UTIF.decodeImage(arrayBuffer, tiffImage);
    }
    const page = ifds[this.currentPage() - 1];
    const rgba = UTIF.toRGBA8(page);

    const width = page.width;
    const height = page.height;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;
    canvas.width = width;
    canvas.height = height;
    this.canvas = canvas;

    const imageData = new ImageData(new Uint8ClampedArray(rgba), width, height);
    context.putImageData(imageData, 0, 0);

    return canvas.toDataURL();
  }

  /**
   * Generate thumbnails for all pages at once
   * @param base64Content The base64 content of the document
   * @returns Promise<string[]> Array of thumbnail data URLs
   */
  public async generateAllThumbnails(base64Content: string): Promise<string[]> {
    switch (base64Content.charAt(0)) {
      case 'J': // PDF
        return await this.generateAllPdfThumbnails(base64Content);
      case 'S': // TIFF
        return await this.generateAllTiffThumbnails(base64Content);
      case 'i': // PNG
      case '/': // JPEG
        return ['data:image/png;base64,' + base64Content];
      default:
        throw new Error('Unsupported file type');
    }
  }

  /**
   * Generates thumbnails for all PDF pages
   * @param base64Pdf The base64 encoded PDF content
   * @returns Promise resolving to array of thumbnail data URLs
   */
  private async generateAllPdfThumbnails(base64Pdf: string): Promise<string[]> {
    const url = URL.createObjectURL(this.base64ToBlob(base64Pdf));

    try {
      const loadingTask = getDocument(url);
      const pdf = await loadingTask.promise;
      const thumbnails: string[] = [];
      const scale = 0.5;

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d') as CanvasRenderingContext2D;

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport }).promise;
        thumbnails.push(canvas.toDataURL());
      }

      return thumbnails;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Generates thumbnails for all TIFF pages efficiently
   * @param base64Tiff The base64 encoded TIFF content
   * @returns Promise resolving to array of thumbnail data URLs
   */
  private async generateAllTiffThumbnails(base64Tiff: string): Promise<string[]> {
    const url = URL.createObjectURL(this.base64ToBlob(base64Tiff));
    let response: Response | null = null;

    try {
      response = await fetch(url);
      const tiffBlob = await response.blob();
      const arrayBuffer = await tiffBlob.arrayBuffer();
      const ifds = UTIF.decode(arrayBuffer);

      // Decode all images at once
      for (const tiffImage of ifds) {
        UTIF.decodeImage(arrayBuffer, tiffImage);
      }

      const thumbnails: string[] = [];
      const scale = 0.5;

      for (const page of ifds) {
        const rgba = UTIF.toRGBA8(page);
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d') as CanvasRenderingContext2D;

        canvas.width = page.width * scale;
        canvas.height = page.height * scale;

        const imageData = new ImageData(new Uint8ClampedArray(rgba), page.width, page.height);
        const tempCanvas = document.createElement('canvas');
        const tempContext = tempCanvas.getContext('2d') as CanvasRenderingContext2D;
        tempCanvas.width = page.width;
        tempCanvas.height = page.height;
        tempContext.putImageData(imageData, 0, 0);

        context.drawImage(tempCanvas, 0, 0, canvas.width, canvas.height);
        thumbnails.push(canvas.toDataURL());
      }

      return thumbnails;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Converts a base64 string to a Blob object
   * @param base64 The base64 string to convert
   * @returns The Blob object
   */
  private base64ToBlob(base64: string): Blob {
    const type: string = 'image/png';
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type });
  }

  /**
   * Creates a new OpenLayers map with the provided image
   * @param imageUrl The URL of the image to display in the map
   */
  createMap(imageUrl: string) {
    this.getImageDimensions(imageUrl)
      .then(({ height, width }) => {
        this.minimumZoom = this.getMinimumZoom(height, width);
        this.extent = [0, 0, width, height];
        const projection = this.createProjection(this.extent);

        this.highlightSource = new VectorSource();
        this.highlightLayer = new VectorLayer({
          source: this.highlightSource,
          style: new Style({
            stroke: new Stroke({
              color: 'red',
              width: 3
            })
          })
        });

        this.map = new Map({
          controls: [],
          layers: [
            new ImageLayer({
              source: new Static({
                url: imageUrl,
                projection: projection,
                imageExtent: this.extent
              })
            }),
            this.highlightLayer
          ],
          target: 'map-viewer',
          view: new View({
            projection: projection,
            center: getCenter(this.extent),
            zoom: this.minimumZoom,
            minZoom: this.minimumZoom,
            maxZoom: 4
          })
        });
      })
      .catch(error => console.error('[FILELOADER] Error in createMap:', error));
  }

  /**
   * Updates the existing map with a new image
   * @param imageUrl The URL of the new image to display
   */
  private updateMap(imageUrl: string) {
    this.getImageDimensions(imageUrl)
      .then(({ height, width }) => {
        this.minimumZoom = this.getMinimumZoom(height, width);
        this.extent = [0, 0, width, height];

        const projection = this.createProjection(this.extent);
        const newLayer = new ImageLayer({
          source: new Static({
            url: imageUrl,
            projection: projection,
            imageExtent: this.extent
          })
        });
        this.map.getLayers().clear();

        this.map.setView(
          new View({
            projection: projection,
            center: getCenter(this.extent),
            zoom: this.minimumZoom,
            minZoom: this.minimumZoom,
            maxZoom: 4
          })
        );

        this.map.addLayer(newLayer);
        this.map.addLayer(this.highlightLayer);
      })
      .catch(error => console.error('[FILELOADER] Error in updateMap:', error));
  }

  /**
   * Calculates the minimum zoom level to fit the image in the container
   * @param imageHeight The height of the image in pixels
   * @param imageWidth The width of the image in pixels
   * @returns The minimum zoom level
   */
  private getMinimumZoom(imageHeight: number, imageWidth: number) {
    const mapElement = document.getElementById('map-viewer');
    if (!mapElement) {
      return 1; // Return a default zoom level if element doesn't exist
    }

    const containerAspectRatio = mapElement.clientHeight / mapElement.clientWidth;
    const imageAspectRatio = imageHeight / imageWidth;
    if (imageAspectRatio > containerAspectRatio) {
      // Fit to width
      return Math.LOG2E * Math.log(mapElement.clientHeight / 256);
    } else {
      // Fit to height
      return Math.LOG2E * Math.log(mapElement.clientWidth / 256);
    }
  }

  /**
   * Gets the dimensions of an image from its URL
   * @param blobUrl The URL of the image
   * @returns Promise resolving to the image dimensions
   */
  private getImageDimensions(blobUrl: string): Promise<{ height: number; width: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve({
          height: img.naturalHeight,
          width: img.naturalWidth
        });
      };
      img.onerror = reject;
      img.src = blobUrl;
    });
  }

  /**
   * Creates a custom projection for the image
   * @param imageExtent The extent of the image
   * @returns The custom projection
   */
  private createProjection(imageExtent: Extent) {
    return new Projection({
      code: 'xkcd-image',
      units: 'pixels',
      extent: imageExtent
    });
  }

  /**
   * Navigates to the next page of the document
   * @param base64Content The base64 content of the document
   */
  public nextPage(base64Content: string) {
    switch (base64Content.charAt(0)) {
      case 'J': //PDF
        this.nextPagePdf(base64Content);
        break;
      case 'S': //TIFF
        this.nextPageTiff(base64Content);
        break;
    }
  }

  /**
   * Navigates to the previous page of the document
   * @param base64Content The base64 content of the document
   */
  public previousPage(base64Content: string) {
    switch (base64Content.charAt(0)) {
      case 'J': //PDF
        this.previousPagePdf(base64Content);
        break;
      case 'S': //TIFF
        this.previousPageTiff(base64Content);
        break;
    }
  }

  /**
   * Navigates to the next page of a PDF document
   * @param base64Pdf The base64 encoded PDF content
   */
  private nextPagePdf(base64Pdf: string) {
    if (this.currentPage() < this.numPages()) {
      this.currentPage.update(p => p + 1);
      this.clearHighlightBoxes();
      this.loadPdf(base64Pdf);
    }
  }

  /**
   * Navigates to the previous page of a PDF document
   * @param base64Pdf The base64 encoded PDF content
   */
  private previousPagePdf(base64Pdf: string) {
    if (this.currentPage() > 1) {
      this.currentPage.update(p => p - 1);
      this.clearHighlightBoxes();
      this.loadPdf(base64Pdf);
    }
  }

  /**
   * Navigates to the next page of a TIFF document
   * @param base64Tiff The base64 encoded TIFF content
   */
  private nextPageTiff(base64Tiff: string) {
    if (this.currentPage() < this.numPages()) {
      this.currentPage.update(p => p + 1);
      this.clearHighlightBoxes();
      this.loadTiff(base64Tiff);
    }
  }

  /**
   * Navigates to the previous page of a TIFF document
   * @param base64Tiff The base64 encoded TIFF content
   */
  private previousPageTiff(base64Tiff: string) {
    if (this.currentPage() > 1) {
      this.currentPage.update(p => p - 1);
      this.clearHighlightBoxes();
      this.loadTiff(base64Tiff);
    }
  }

  /**
   * Zooms in on the current view
   */
  public zoomIn() {
    this.map.getView().setZoom((this.map?.getView().getZoom() || 0) + 0.3);
  }

  /**
   * Zooms out from the current view
   */
  public zoomOut() {
    this.map.getView().setZoom((this.map?.getView().getZoom() || 0) - 0.3);
  }

  /**
   * Recenters the view and resets zoom to minimum
   */
  public recenter() {
    this.map.getView().setZoom(0);
    this.map.getView().setCenter(getCenter(this.extent));
  }

  /**
   * Rotates the view by 90 degrees clockwise
   */
  public rotate() {
    this.rotationIndex = (this.rotationIndex + 1) % this.rotationValues.length;
    this.rotation = this.rotationValues[this.rotationIndex];
    this.setRotation();
  }

  /**
   * Applies the current rotation to the map view
   */
  private setRotation() {
    this.map.getView().setRotation(this.rotation);
  }

  /**
   * Clears the map by removing the image source
   */
  public clearMap() {
    this.map.getLayers().getArray()[0]?.unset('source');
  }

  /**
   * Destroys the map instance and resets it
   */
  public destroyMap() {
    this.map.setTarget(undefined);
    this.map = new Map();
  }

  private highlightedBoxes: Array<{
    vertices: { x: number; y: number }[];
    page: number;
    color: string;
  }> = [];

  /**
   * Highlight a field using its geometry data
   * @param fieldInfo The field information containing geometry data
   * @param color The color to use for highlighting (default: 'red')
   */
  highlightField(fieldInfo: any, color: string = 'red'): void {
    this.clearHighlightBoxes();

    if (!fieldInfo || !fieldInfo.geometry || !fieldInfo.geometry.length) {
      console.warn('No geometry information available for this field');
      return;
    }

    fieldInfo.geometry.forEach((geo: any) => {
      if (geo.vertices && geo.vertices.length === 4) {
        this.drawBoundingBox(geo.vertices, geo.page, color);
      }
    });
  }

  /**
   * Draws a bounding box on the map using the provided vertices
   * @param vertices Array of four vertices defining the bounding box
   * @param page The page number where the box should be drawn
   * @param color The color of the bounding box (default: 'red')
   * @param paddingPixels Additional padding around the box in pixels (default: 4)
   */
  drawBoundingBox(
    vertices: { x: number; y: number }[],
    page: number,
    color: string = '#DD3137',
    paddingPixels: number = 4
  ): void {
    if (!this.map || !vertices || vertices.length !== 4) {
      console.error('Invalid map or vertices');
      return;
    }

    if (this.getCurrentPage !== page) {
      return;
    }

    const size = this.map.getSize();
    if (!size) {
      console.error('Unable to get map size');
      return;
    }

    const width = this.extent[2] - this.extent[0];
    const height = this.extent[3] - this.extent[1];

    const paddingX = paddingPixels / width;
    const paddingY = paddingPixels / height;

    const paddedVertices = [
      {
        x: Math.max(0, vertices[0].x - paddingX),
        y: Math.max(0, vertices[0].y - paddingY)
      },
      {
        x: Math.min(1, vertices[1].x + paddingX),
        y: Math.max(0, vertices[1].y - paddingY)
      },
      {
        x: Math.min(1, vertices[2].x + paddingX),
        y: Math.min(1, vertices[2].y + paddingY)
      },
      {
        x: Math.max(0, vertices[3].x - paddingX),
        y: Math.min(1, vertices[3].y + paddingY)
      }
    ];

    const coords = [];
    for (const vertex of paddedVertices) {
      // Convert from normalized to pixel coordinates
      coords.push([this.extent[0] + vertex.x * width, this.extent[3] - vertex.y * height]);
    }
    coords.push([coords[0][0], coords[0][1]]);
    const polygonFeature = new Feature({
      geometry: new Polygon([coords])
    });

    polygonFeature.setStyle(
      new Style({
        stroke: new Stroke({
          color: color,
          width: 2
        })
      })
    );

    this.highlightSource.addFeature(polygonFeature);

    this.map.render();
  }

  /**
   * Clear all highlights
   */
  clearHighlightBoxes(): void {
    if (this.highlightSource) {
      this.highlightSource.clear();
      this.map?.render();
    }
  }

  /**
   * Sets the visibility of PDF annotations and re-renders the current page
   * @param visible Whether annotations should be visible
   */
  setAnnotationsVisible(visible: boolean): void {
    this.annotationsVisible.set(visible);
    this.refreshView();
  }

  /**
   * Navigate to a specific page
   * @param base64Content The base64 content of the document
   * @param pageNumber The page number to navigate to
   */
  public goToPage(base64Content: string, pageNumber: number): void {
    if (pageNumber <= 0 || pageNumber > this.numPages()) {
      console.warn(`Invalid page number ${pageNumber}. Valid range: 1-${this.numPages()}`);
      return;
    }

    if (this.currentPage() === pageNumber) {
      return;
    }
    this.clearHighlightBoxes();

    this.currentPage.set(pageNumber);

    switch (base64Content.charAt(0)) {
      case 'J': // PDF
        this.loadPdf(base64Content);
        break;
      case 'S': // TIFF
        this.loadTiff(base64Content);
        break;
      case 'i': // PNG
      case '/': // JPEG
        break;
      default:
        console.warn('Unsupported file type for page navigation');
        break;
    }
  }
}
